import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { CalendarBlank, Clock, Coffee, Note, PaperPlaneTilt, MapPin, NavigationArrow, Buildings, Trash, Plus } from '@phosphor-icons/react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { defaultHoursForDate, getWorkingHours } from '../components/time-entries/AdminPunchModal';
import PostPointageArticles from '../components/time-entries/PostPointageArticles';
import { fmtDateFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TimeEntryPage = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const _initialDefaults = defaultHoursForDate(format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState(_initialDefaults.start_time);
  const [endTime, setEndTime] = useState(_initialDefaults.end_time);
  const [breakDuration, setBreakDuration] = useState(_initialDefaults.break_duration);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [availableProjects, setAvailableProjects] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [myVehicles, setMyVehicles] = useState([]);
  const [dayEntries, setDayEntries] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);
  // Post-pointage articles selection modal
  const [postPointage, setPostPointage] = useState(null);
  
  // Location state
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: null,
    loading: false,
    error: null
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/users/me/projects`, { withCredentials: true })
      .then(res => setAvailableProjects(res.data || []))
      .catch(() => toast.error('Erreur chargement chantiers'));
    axios.get(`${API_URL}/api/vehicles/my-vehicles`, { withCredentials: true })
      .then(res => {
        const list = res.data || [];
        setMyVehicles(list);
        if (list.length === 1) setVehicleId(list[0].id); // auto-select if only one
      })
      .catch(() => { /* employee may not have vehicles - silent */ });
  }, []);

  // Whenever the picked date changes, reset start/end/break to BATI-BOYS defaults for that weekday
  useEffect(() => {
    if (!date) return;
    // Make sure the working-hours cache is populated, then apply defaults.
    getWorkingHours().then(() => {
      const iso = format(date, 'yyyy-MM-dd');
      const d = defaultHoursForDate(iso);
      setStartTime(d.start_time);
      setEndTime(d.end_time);
      setBreakDuration(d.break_duration);
    });
  }, [date]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocation(prev => ({ ...prev, error: 'Géolocalisation non supportée par ce navigateur' }));
      return;
    }

    setLocation(prev => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Try to get address from coordinates using reverse geocoding
        let address = null;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { 'Accept-Language': 'fr' } }
          );
          const data = await response.json();
          if (data.display_name) {
            address = data.display_name;
          }
        } catch (e) {
          console.error('Erreur geolocalisation reverse:', e);
        }

        setLocation({
          latitude,
          longitude,
          address,
          loading: false,
          error: null
        });
        toast.success('Position récupérée avec succès');
      },
      (error) => {
        let errorMessage = 'Erreur de géolocalisation';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Accès à la position refusé. Veuillez autoriser la géolocalisation.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Position indisponible';
            break;
          case error.TIMEOUT:
            errorMessage = 'Délai d\'attente dépassé';
            break;
          default:
            break;
        }
        setLocation(prev => ({ ...prev, loading: false, error: errorMessage }));
        toast.error(errorMessage);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // Fetch entries for the selected day (so user sees what's already clocked + can detect overlaps visually)
  const fetchDayEntries = useCallback(async (d) => {
    setLoadingDay(true);
    try {
      const iso = format(d, 'yyyy-MM-dd');
      const res = await axios.get(`${API_URL}/api/time-entries`, { params: { start_date: iso, end_date: iso }, withCredentials: true });
      setDayEntries((res.data || []).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    } catch { setDayEntries([]); }
    setLoadingDay(false);
  }, []);

  useEffect(() => { fetchDayEntries(date); }, [date, fetchDayEntries]);

  // Helper: check if a [start, end] overlaps with any existing day entry (client-side hint)
  const hasOverlap = (start, end) => dayEntries.some(e => e.start_time < end && e.end_time > start);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) {
      toast.error('Veuillez choisir un chantier');
      return;
    }
    if (myVehicles.length > 0 && !vehicleId) {
      toast.error('Veuillez selectionner le vehicule utilise');
      return;
    }
    if (hasOverlap(startTime, endTime)) {
      toast.error('Cette plage horaire chevauche un pointage deja enregistre pour cette journee. Choisissez d\'autres horaires.');
      return;
    }
    setLoading(true);

    try {
      const r = await axios.post(`${API_URL}/api/time-entries`, {
        date: format(date, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: endTime,
        break_duration: parseInt(breakDuration),
        notes: notes || null,
        latitude: location.latitude,
        longitude: location.longitude,
        location_address: location.address,
        project_id: projectId,
        vehicle_id: vehicleId || null
      }, { withCredentials: true });

      toast.success('Pointage enregistre - identifiez les articles realises');
      // Open the post-pointage articles selection modal
      setPostPointage({
        projectId,
        date: format(date, 'yyyy-MM-dd'),
        timeEntryId: r.data?.id || null,
      });
      // Reset only the time/project/notes, keep date and vehicle so user can chain another entry quickly
      const defaults = defaultHoursForDate(format(date, 'yyyy-MM-dd'));
      setStartTime(defaults.start_time);
      setEndTime(defaults.end_time);
      setBreakDuration(defaults.break_duration);
      setNotes('');
      setProjectId('');
      fetchDayEntries(date);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(typeof message === 'string' ? message : 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDayEntry = async (id) => {
    if (!window.confirm('Supprimer ce pointage ?')) return;
    try {
      await axios.delete(`${API_URL}/api/time-entries/${id}`, { withCredentials: true });
      toast.success('Pointage supprime');
      fetchDayEntries(date);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const calculateHours = () => {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diff = (end - start) / 1000 / 60 / 60;
    return Math.max(0, diff - (breakDuration / 60)).toFixed(2);
  };

  return (
    <div data-testid="time-entry-page">
      <div className="page-header">
        <h1 className="page-title">Saisir mes heures</h1>
        <p className="page-subtitle">Enregistrez vos heures de travail pour validation</p>
      </div>

      <div className="card" style={{ maxWidth: '600px' }}>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                <CalendarBlank size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Date
              </label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="date-picker-trigger"
                    className="form-input"
                    style={{ 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    {format(date, 'dd/MM/yyyy')}
                    <CalendarBlank size={20} color="#5E5F66" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { if (d) { setDate(d); setCalendarOpen(false); } }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Buildings size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Chantier <span style={{ color: '#C62828' }}>*</span>
              </label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="time-entry-project-select">
                  <SelectValue placeholder={availableProjects.length === 0 ? 'Aucun chantier disponible' : 'Selectionner un chantier'} />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {availableProjects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.client_name ? ` - ${p.client_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableProjects.length === 0 && (
                <small style={{ color: '#EF6C00', fontSize: '0.75rem' }}>Aucun chantier ne vous est affecte. Contactez votre administrateur.</small>
              )}
            </div>

            {myVehicles.length > 0 && (
              <div className="form-group">
                <label className="form-label">
                  <NavigationArrow size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Vehicule utilise <span style={{ color: '#C62828' }}>*</span>
                </label>
                <Select value={vehicleId} onValueChange={setVehicleId}>
                  <SelectTrigger data-testid="vehicle-select">
                    <SelectValue placeholder="Selectionner le vehicule utilise" />
                  </SelectTrigger>
                  <SelectContent>
                    {myVehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.brand} {v.model}{v.registration ? ` - ${v.registration}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <small style={{ color: '#5E5F66', fontSize: '0.75rem' }}>Vehicule utilise pour vous rendre sur le chantier.</small>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label className="form-label">
                  <Clock size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Heure de début
                </label>
                <input
                  data-testid="start-time-input"
                  type="time"
                  className="form-input"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Clock size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Heure de fin
                </label>
                <input
                  data-testid="end-time-input"
                  type="time"
                  className="form-input"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Coffee size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Pause (minutes)
              </label>
              <input
                data-testid="break-duration-input"
                type="number"
                className="form-input"
                value={breakDuration}
                onChange={(e) => setBreakDuration(e.target.value)}
                min="0"
                max="480"
              />
            </div>

            {/* Location Section */}
            <div className="form-group">
              <label className="form-label">
                <MapPin size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Localisation
              </label>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#F4F5F7', 
                borderRadius: '4px',
                border: '1px solid #E2E2E6'
              }}>
                {location.latitude && location.longitude ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <NavigationArrow size={18} color="#00C853" weight="fill" />
                      <span style={{ fontWeight: '600', color: '#00C853' }}>Position enregistrée</span>
                    </div>
                    {location.address && (
                      <p style={{ fontSize: '0.875rem', color: '#5E5F66', margin: '8px 0' }}>
                        {location.address}
                      </p>
                    )}
                    <p style={{ fontSize: '0.75rem', color: '#5E5F66' }}>
                      Lat: {location.latitude.toFixed(6)}, Lng: {location.longitude.toFixed(6)}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#5E5F66', marginBottom: '12px' }}>
                      Enregistrez votre position pour le pointage
                    </p>
                    <button
                      type="button"
                      data-testid="get-location-btn"
                      className="btn btn-secondary btn-sm"
                      onClick={getLocation}
                      disabled={location.loading}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <MapPin size={16} />
                      {location.loading ? 'Récupération...' : 'Récupérer ma position'}
                    </button>
                    {location.error && (
                      <p style={{ fontSize: '0.75rem', color: '#C62828', marginTop: '8px' }}>
                        {location.error}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Note size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Notes (optionnel)
              </label>
              <textarea
                data-testid="notes-input"
                className="form-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Commentaires sur votre journée..."
                style={{ resize: 'vertical' }}
              />
            </div>

            <div style={{ 
              backgroundColor: '#F4F5F7', 
              padding: '16px', 
              borderRadius: '2px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span className="label-text">Total heures travaillées</span>
              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#002FA7' }}>
                {calculateHours()}h
              </span>
            </div>

            <button
              data-testid="submit-time-entry-btn"
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={loading}
            >
              <PaperPlaneTilt size={18} />
              {loading ? 'Enregistrement...' : (dayEntries.length > 0 ? 'Ajouter ce pointage' : 'Enregistrer mes heures')}
            </button>
          </form>
        </div>
      </div>

      {/* Pointages deja enregistres pour la date selectionnee */}
      <div className="card" style={{ maxWidth: '600px', marginTop: '16px' }} data-testid="day-entries-section">
        <div className="card-body">
          <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={18} weight="bold" color="#002FA7" />
            Pointages du {fmtDateFR(format(date, 'yyyy-MM-dd'))}
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#5E5F66', fontWeight: 500 }}>
              {dayEntries.length} pointage(s) - Total : {dayEntries.reduce((s, e) => s + (e.total_hours || 0), 0).toFixed(2)} h
            </span>
          </h3>
          {loadingDay ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#5E5F66', fontSize: '0.85rem' }}>Chargement...</div>
          ) : dayEntries.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#5E5F66', fontSize: '0.85rem' }}>
              Aucun pointage enregistre pour cette journee. Renseignez le formulaire ci-dessus pour ajouter votre premier creneau.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dayEntries.map(e => {
                const statusColor = e.status === 'validated' ? '#2E7D32' : e.status === 'rejected' ? '#C62828' : '#E65100';
                const statusBg = e.status === 'validated' ? '#E8F5E9' : e.status === 'rejected' ? '#FFEBEE' : '#FFF3E0';
                const statusLabel = { validated: 'Valide', rejected: 'Refuse', pending: 'En attente' }[e.status] || e.status;
                return (
                  <div key={e.id} data-testid={`day-entry-${e.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F4F5F7', border: '1px solid #E2E2E6', borderRadius: 8, fontSize: '0.85rem' }}>
                    <div style={{ fontWeight: 700, color: '#002FA7', minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                      {e.start_time} - {e.end_time}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.project_name || 'Sans chantier'}</div>
                      <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>
                        {e.total_hours?.toFixed(2) || 0} h {e.break_duration ? `· Pause ${e.break_duration} min` : ''}
                      </div>
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: 999, background: statusBg, color: statusColor, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>{statusLabel}</span>
                    {e.status === 'pending' && (
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteDayEntry(e.id)} title="Supprimer" data-testid={`day-entry-delete-${e.id}`}>
                        <Trash size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/my-hours')} style={{ marginTop: 6 }} data-testid="go-my-hours-btn">
                Voir l'historique complet
              </button>
            </div>
          )}
        </div>
      </div>
      {postPointage && (
        <PostPointageArticles
          projectId={postPointage.projectId}
          date={postPointage.date}
          timeEntryId={postPointage.timeEntryId}
          onClose={() => setPostPointage(null)}
        />
      )}
    </div>
  );
};

export default TimeEntryPage;
